/* eslint-disable sonarjs/cognitive-complexity */
import type { SupportedAlpha2CountryCode } from '@/lib/utils/localization/getSupportedAlpha2CountryCodes'
import type { SupportedAlpha3CountryCode } from '@/lib/utils/localization/getSupportedAlpha3CountryCodes'
import type { SupportedLanguage } from '@/lib/utils/localization/getSupportedLanguages'

import { getSupportedAlpha2CountryCodes } from '@/lib/utils/localization/getSupportedAlpha2CountryCodes'
import { isSupportedAlpha2CountryCode } from '@/lib/utils/localization/isSupportedAlpha2CountryCode'
import { isSupportedLanguage } from '@/lib/utils/localization/isSupportedLanguage'
import { toAlpha2 } from '@/lib/utils/localization/toAlpha2'

import useGlobalStore from '@/store/useGlobalStore'

import { getHeaderData } from '@/lib/cms/getHeaderData'
import type { SupportedLocale } from '@/lib/utils/localization/getFlattenedLocales'
import { getCfHeaderCookie } from '@/lib/utils/shared/cookies/getCfHeaderCookie'
import { getVercelHeaderCookie } from '@/lib/utils/shared/cookies/getVercelHeaderCookie'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { getMarketConfig } from '@/constants/MarketExceptions/utils/getMarketConfig'
import packageJson from '../../../../package.json'

export const useInitApp = () => {
  const router = useRouter()
  const {
    setApp,
    setCMS,
    user: { isSignedIn },
  } = useGlobalStore()

  useEffect(() => {
    // This will keep the url locale in sync with the app (Zustand) locale, for easier access
    // we should put this in a hook where we initialize client side state
    if (!router.query.alpha3) return
    if (!router.query.language) return
    ;(async () => {
      function handleLanguageParameter(
        parameter: string | string[],
      ): SupportedLanguage | undefined {
        let language: string

        if (Array.isArray(parameter)) {
          ;[language] = parameter
        } else {
          language = parameter
        }

        if (isSupportedLanguage(language)) {
          return language
        }

        return 'en' // or a default supported language, e.g., 'en'
      }

      const handleAlpha2Parameter = async (
        parameter: string | string[],
      ): Promise<SupportedAlpha2CountryCode | undefined> => {
        let alpha2: string

        if (Array.isArray(parameter)) {
          ;[alpha2] = parameter
        } else {
          alpha2 = parameter
        }

        if (
          isSupportedAlpha2CountryCode(alpha2) &&
          getSupportedAlpha2CountryCodes().includes(alpha2)
        ) {
          return alpha2
        }

        return 'US' // or a default supported alpha2 code
      }

      const alpha2 = (await handleAlpha2Parameter(
        toAlpha2(router.query.alpha3 as SupportedAlpha3CountryCode) || 'US',
      )) as SupportedAlpha2CountryCode

      const locale = `${router.query.language as SupportedLanguage}-${toAlpha2(
        router.query.alpha3 as SupportedAlpha3CountryCode,
      )}` as SupportedLocale

      const cfHeaderCookie = getCfHeaderCookie()
      const vercelHeaderCookie = getVercelHeaderCookie()

      const fetchHeader = async () => {
        const headerData = await getHeaderData({
          locale,
        })

        if (!headerData) {
          // setCMS({ header: 'error' })
          return
        }

        if ('error' in headerData) {
          // setCMS({ header: 'error' })
          return
        }

        setCMS({
          header: headerData,
        })
      }

      try {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        fetchHeader()
      } catch {
        setCMS({ header: 'error' })
      }

      // TODO: remove conditional chaining, its no longer needed since we are always setting the cookies
      const geoIp = {
        zipCode: cfHeaderCookie?.zipCode ?? vercelHeaderCookie?.zipCode ?? null,
        city: cfHeaderCookie?.city ?? vercelHeaderCookie?.city ?? null,
        region: cfHeaderCookie?.region ?? vercelHeaderCookie?.region ?? null,
        latitude:
          cfHeaderCookie?.latitude ?? vercelHeaderCookie?.latitude ?? null,
        longitude:
          cfHeaderCookie?.longitude ?? vercelHeaderCookie?.longitude ?? null,
      }

      setApp({
        // When isUseCosmic is null, that means the flag has not loaded yet, so we can safely default to the cached value
        // to prevent unwanted re-renders when the value changes from null to the actual value, which would happen milliseconds after
        // the first rerender.
        version: packageJson.version,
        locale,
        alpha3: router.query.alpha3 as SupportedAlpha3CountryCode,
        alpha2,
        language: handleLanguageParameter(router.query.language || 'en'),
        marketExceptions: getMarketConfig(alpha2),
        geoIp,
      })
    })()
  }, [router.query.alpha3, router.query.language, setApp, isSignedIn, setCMS])
}
